// Finance pricing API. Admin only, so it uses the admin apiClient: /finance has no HttpClientProvider.
// apiClient throws an HttpError (an Error carrying the server's message) on a 4xx.
import { apiClient } from "@/services/apiClient";
import type { CatalogItem, ContractsResponse, ListiniResponse, Listino, ListinoLine, ListinoLineInput, ServiceLine, ServiceLinePatch, WorkType } from "../types";

const BASE = "/api/finance";

/** The catalogs a price splits over (TD-075), by their path. */
export type Catalog = "work-types" | "service-lines";

/** What changes on a listino (TD-083): its name and its start date, any day. */
export interface ListinoChanges {
  name?: string;
  startDate?: string;
}

/** A new listino: the one it copies (the active one when omitted), its name and its start, checked as an edit is. */
export interface NewListinoInput {
  copyOf?: string;
  name: string;
  startDate: string;
}

export const pricingService = {
  workTypes: () => apiClient.get<{ workTypes: WorkType[] }>(`${BASE}/work-types`).then((r) => r.workTypes),
  createWorkType: (label: string) => apiClient.post<WorkType>(`${BASE}/work-types`, { label }),
  serviceLines: () => apiClient.get<{ serviceLines: ServiceLine[] }>(`${BASE}/service-lines`).then((r) => r.serviceLines),
  createServiceLine: (label: string) => apiClient.post<ServiceLine>(`${BASE}/service-lines`, { label }),
  /** A service line's name and split defaults (TD-078), any subset in one request. */
  updateServiceLine: (id: string, patch: ServiceLinePatch) => apiClient.patch<ServiceLine>(`${BASE}/service-lines/${id}`, patch),
  renameCatalogRow: (catalog: Catalog, id: string, label: string) => apiClient.patch<CatalogItem>(`${BASE}/${catalog}/${id}`, { label }),
  deleteCatalogRow: (catalog: Catalog, id: string) => apiClient.delete<void>(`${BASE}/${catalog}/${id}`),
  listini: () => apiClient.get<ListiniResponse>(`${BASE}/listini`),
  /** A copy of `copyOf`, else of the active listino, with its name and start in the same request (TD-083). */
  createListino: (input: NewListinoInput) => apiClient.post<Listino>(`${BASE}/listini`, input),
  updateListino: (id: string, changes: ListinoChanges) => apiClient.patch<Listino>(`${BASE}/listini/${id}`, changes),
  deleteListino: (id: string) => apiClient.delete<void>(`${BASE}/listini/${id}`),
  addListinoLine: (id: string, line: ListinoLineInput) => apiClient.post<ListinoLine>(`${BASE}/listini/${id}/lines`, line),
  updateListinoLine: (id: string, lineId: string, line: ListinoLineInput) => apiClient.put<ListinoLine>(`${BASE}/listini/${id}/lines/${lineId}`, line),
  removeListinoLine: (id: string, lineId: string) => apiClient.delete<void>(`${BASE}/listini/${id}/lines/${lineId}`),
  clientContracts: (clientId: string) => apiClient.get<ContractsResponse>(`${BASE}/clients/${clientId}/contracts`),
};
